//this file is AppointmentForm.jsx

import React, { useContext } from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import ModalButtons from 'src/components/UI/button/ModalButtons';
import { APICONSTANTS } from 'src/components/Constants';

import TextInput from 'src/components/UI/input/TextInput';

import axios from 'src/axios';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import DateInput from 'src/components/UI/input/DateInput';
import AssociatedNote from 'src/components/UI/input/AssociatedNote';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Tooltip,
  InputAdornment
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import { FormControl } from '@mui/material';
import { calculateAgeOnDate, formatDate } from 'src/util/util';
import { toLocalDateFromPickerOrImport } from 'src/util/partialDate';
import {
  SnackContext,
  ThemeContext,
  AppContext,
  UserContext
} from 'src/store/ContextStore';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import {
  getReferralTags,
  getDependencies,
  getClinicalDefaults,
  saveClinicalDefault,
  deleteClinicalDefault
} from './formService';
import { fallbackDefaultLabel } from 'src/util/statusSemantics';
import AppointmentStatusField from './AppointmentStatusField';
import AutocompleteInput from 'src/components/UI/input/AutocompleteInput';

import UploadFileList from 'src/components/UI/input/UploadFileList';
import { useAttachmentChanges } from 'src/components/UI/input/attachments';
import ClinicalNoteDeleteModal from '../../diagnoses-menu/ClinicalNoteDeleteModal';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import { useClinicalSuggestions } from 'src/hooks/useClinicalSuggestions';
import ClinicalSuggestionsPanel from 'src/components/UI/ClinicalSuggestionsPanel';

export default function AppointmentForm({
  data,
  handleClose,
  familyMemberId,
  isProband,
  isEditing,
  model,
  staffMembers,
  staffMemberOptions,
  isDisabled,
  setUnsavedChanges,
  refreshPedigreeLabels
}) {
  const { content } = React.useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;
  const { user: loggedInUser } = React.useContext(UserContext);
  const { setSnack } = React.useContext(SnackContext);
  const [open, setOpen] = React.useState(false);

  const clinicalSuggestions = useClinicalSuggestions({
    familyMemberId,
    isProband
  });
  const {
    analyzingNote,
    handleAttachmentText,
    analyzeText,
    acceptSuggestions
  } = clinicalSuggestions;
  const [loading, setLoading] = React.useState(false);

  const patientDob = data?.dob;
  // The age on the calendar day of the appointment date (calculateAgeOnDate).
  // The date is handed over as stored — a picker instant, Calendly's start time
  // or a pathway's server new Date(), or UTC midnight from the spreadsheet
  // importers and demo seed — and only the raw value says which, so it is read
  // by toLocalDateFromPickerOrImport. `calculateAge` counted elapsed time from
  // a UTC-midnight birth date: a year short on the birthday at any positive
  // offset, and a positive age for a date before the birth, which is now blank.
  // The date field shows the day it read (asPickerDay), so the day on screen is
  // the day the age is from.
  const asPickerDay = toLocalDateFromPickerOrImport;
  const computeAgeAtRecord = React.useCallback(
    (appointmentDate) => calculateAgeOnDate(patientDob, appointmentDate),
    [patientDob]
  );

  // 处理表单初始值
  const appointmentDate = new Date();
  const defaultValues = isEditing
    ? {
        ...model,
        fileAttachments: model.fileAttachments || [],
        // The TimePicker (dayjs adapter) needs a dayjs object; the saved record
        // stores appointmentTime serialized, so re-wrap it on edit-load —
        // otherwise the picker calls value.isValid() on a string and crashes.
        appointmentTime: model.appointmentTime
          ? dayjs(model.appointmentTime)
          : dayjs(new Date()),
        // 确保这三个值存在
        joinUrl: model.joinUrl || '',
        rescheduleUrl: model.rescheduleUrl || '',
        cancelUrl: model.cancelUrl || ''
      }
    : {
        familyId: user.familyId,
        familyMemberId: familyMemberId,
        clinicName: '',
        appointmentId: '',
        appointmentDate: appointmentDate,
        ageAtRecord: computeAgeAtRecord(appointmentDate),
        appointmentTime: dayjs(new Date()), // TimePicker 使用 dayjs
        clinicianName: '',
        // The shipped default (Booked) until the group's own list arrives;
        // AppointmentStatusField then moves it to the group's default row if
        // that differs (TGCLOUD-1751). It used to be the literal 'Closed'.
        appointmentStatus: fallbackDefaultLabel('appointmentStatuses'),
        statusReason: '',
        appointmentType: '',
        location: 'Telephone',
        associatedReferral: { label: 'none', _id: '' },
        fileAttachments: [],
        appointmentNotes: '',
        showInFamily: false,
        staffMember: [loggedInUser._id],
        joinUrl: '',
        rescheduleUrl: '',
        cancelUrl: '',
        inviteeName: ''
      };

  const { theme } = useContext(ThemeContext);
  const [formValues, setFormValues] = React.useState(defaultValues);
  // Attachment deletes wait for a successful save (TGCLOUD-1850).
  const attachmentChanges = useAttachmentChanges(defaultValues.fileAttachments);
  const [uploadingFiles, setUploadingFiles] = React.useState(false);

  React.useEffect(() => {
    if (!patientDob) return;
    const nextAge = computeAgeAtRecord(formValues.appointmentDate);
    setFormValues((prev) => {
      const currentAge = prev.ageAtRecord === undefined ? '' : prev.ageAtRecord;
      if (nextAge === '' && currentAge === '') return prev;
      if (nextAge === currentAge) return prev;
      return { ...prev, ageAtRecord: nextAge };
    });
  }, [patientDob, computeAgeAtRecord, formValues.appointmentDate]);

  const [typeOptions, setTypeOptions] = React.useState([]);

  React.useEffect(() => {
    getClinicalDefaults('appointmentTypes').then(setTypeOptions);
  }, []);

  // The status and its reason come from AppointmentStatusField, which reports
  // only the fields that changed. Its own move onto the group's default for a
  // new record is not the user's edit, so it does not mark the form dirty.
  const handleStatusChange = React.useCallback(
    (patch, { userInitiated } = {}) => {
      setFormValues((prevValues) => ({ ...prevValues, ...patch }));
      if (userInitiated) {
        setUnsavedChanges((prev) => ({ ...prev, edit: true }));
      }
    },
    [setUnsavedChanges]
  );

  // TGCLOUD-1788 — same dead-state defect as Consent Type and Contact Mode: no
  // setter, no fetch, so a location typed here was never offered again. The four
  // values above are the shipped seed for `appointmentLocations` (declared in
  // backend/config/lookupDefinitions.js since TGCLOUD-1784), and the appbar
  // appointment modal now reads the same key instead of its own private copy.
  const [locationOptions, setLocationOptions] = React.useState([]);

  React.useEffect(() => {
    getClinicalDefaults('appointmentLocations').then(setLocationOptions);
  }, []);

  let [referralOptions, setReferralOptions] = React.useState(['loading...']);

  React.useEffect(() => {
    const fetchData = async () => {
      const referralTags = await getReferralTags(
        theme,
        familyMemberId,
        formValues.familyId
      );
      setReferralOptions(referralTags);
    };
    if (referralOptions.length === 1 && referralOptions[0] === 'loading...') {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralOptions]);

  const handleCloseModal = () => {
    setOpen(false);
  };
  const handleOpenModal = () => {
    setOpen(true);
  };

  const handleChange = (e) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [e.target.name]: e.target.value
    }));
    setUnsavedChanges((prev) => ({ ...prev, edit: true }));
  };

  const deleteNote = async (e) => {
    setLoading(true);

    const dep = await getDependencies(
      user.familyId,
      model._id,
      theme.dateFormat
    );
    if (dep.length !== 0) {
      setSnack({
        status: true,
        msg: `Associated record exists; check the ${dep[0]}`,
        severity: 'error'
      });
      setLoading(false);
      return;
    }

    axios
      .delete(`/clinical-notes/note`, {
        params: {
          _id: model._id
        }
      })
      .then(() => {
        setSnack({
          status: true,
          msg: 'Record deleted',
          severity: 'success'
        });
        handleCloseModal();
        handleClose({ exit: true });
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: `Delete failed! ${err}`,
          severity: 'error'
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // 显示在家庭切换
  const onToggleSwitch = (e) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [e.target.name]: e.target.checked
    }));
    setUnsavedChanges((prev) => ({ ...prev, edit: true }));
  };

  // 保存逻辑
  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    let staffMember = null;
    if (formValues.staffMember && formValues.staffMember.length > 0) {
      staffMember = formValues.staffMember
        .map((staff) => {
          let existStaffMember = staffMembers.find(
            (member) => member.userId === staff
          );

          return existStaffMember ? { ...existStaffMember } : null;
        })
        .filter(Boolean);
    }

    const parsePayload = {
      ...formValues,
      staffMember: staffMember ? [...staffMember] : []
    };

    return axios
      .put(APICONSTANTS.appointment, parsePayload)
      .then(async (res) => {
        attachmentChanges.commit(parsePayload.fileAttachments, familyMemberId);
        await refreshPedigreeLabels?.();
        handleClose({ exit: true });
        setSnack({
          status: true,
          msg: 'Successfully saved',
          severity: 'success'
        });
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: `Upload failed! ${err}`,
          severity: 'error'
        });
      });
  };

  const allStaffMembers = React.useMemo(() => {
    return staffMemberOptions.map((options) => options.value);
  }, [staffMemberOptions]);

  const staffMembersMap = React.useMemo(() => {
    return staffMemberOptions.reduce((acc, item) => {
      acc[item.value] = item.label;
      return acc;
    }, {});
  }, [staffMemberOptions]);

  return (
    <form className="user-form">
      <ClinicalNoteDeleteModal
        loading={loading}
        open={open}
        handleCloseModal={handleCloseModal}
        onConfirm={deleteNote}
        title="Are you sure you want to delete this Appointment ?"
        content="Warning: This action will permanently delete this Appointment. Once deleted, this
      information will be permanently removed and cannot be restored."
      />
      <Grid container spacing={2}>
        <Grid size={12}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Clinic Name</FormLabel>
          </Grid>
          <Box sx={{ display: 'flex', position: 'relative' }}>
            <TextField
              variant="outlined"
              fullWidth
              onChange={handleChange}
              name="clinicName"
              value={formValues.clinicName}
              slotProps={{
                htmlInput: { readOnly: isDisabled }
              }}
            />
          </Box>
        </Grid>

        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Appointment ID</FormLabel>
          </Grid>
          <Box sx={{ display: 'flex', position: 'relative' }}>
            <TextField
              variant="outlined"
              fullWidth
              onChange={handleChange}
              name="appointmentId"
              value={formValues.appointmentId}
              slotProps={{
                htmlInput: { readOnly: isDisabled }
              }}
            />
          </Box>
        </Grid>

        <Grid size={6}>
          <AppointmentStatusField
            status={formValues.appointmentStatus}
            reason={formValues.statusReason}
            appointmentDate={formValues.appointmentDate}
            appointmentTime={formValues.appointmentTime}
            isNew={!isEditing}
            readOnly={isDisabled}
            onChange={handleStatusChange}
          />
        </Grid>

        <Grid size={6}>
          <TextInput
            type="date"
            label="Appointment Date"
            name="appointmentDate"
            value={asPickerDay(formValues.appointmentDate)}
            onChange={(e) =>
              setFormValues((prevValues) => ({
                ...prevValues,
                appointmentDate: e,
                ageAtRecord: computeAgeAtRecord(e)
              }))
            }
            minDate={new Date(1600, 0, 0)}
            readOnly={isDisabled}
          />
        </Grid>

        <Grid size={6}>
          <TextInput
            label="Age at Appointment"
            name="ageAtRecord"
            type="number"
            value={formValues.ageAtRecord ?? ''}
            inputProps={{ min: 0, className: 'no-spinners' }}
            // Disabled (muted) only when an age is actually being
            // auto-calculated, i.e. DoB AND appointment date both set.
            // Editable when either is missing so a known age can be
            // recorded directly.
            disabled={!!patientDob && !!formValues.appointmentDate}
            onChange={
              patientDob && formValues.appointmentDate
                ? undefined
                : (e) => {
                    const val = e.target.value;
                    const parsed = val === '' ? '' : Number(val);
                    if (val === '' || !Number.isNaN(parsed)) {
                      setFormValues((prev) => ({
                        ...prev,
                        ageAtRecord: val === '' ? '' : parsed
                      }));
                      setUnsavedChanges((prev) => ({ ...prev, edit: true }));
                    }
                  }
            }
          />
        </Grid>

        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Appointment Time</FormLabel>
          </Grid>
          <Box sx={{ display: 'flex', position: 'relative' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                value={
                  dayjs.isDayjs(formValues.appointmentTime)
                    ? formValues.appointmentTime
                    : null
                }
                onChange={(e) => {
                  setFormValues((prevValues) => ({
                    ...prevValues,
                    appointmentTime: e
                  }));
                }}
                readOnly={isDisabled}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Box>
        </Grid>

        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Appointment Type</FormLabel>
          </Grid>
          <AutocompleteInput
            name="appointmentType"
            value={formValues.appointmentType || ''}
            onChange={(newValue) => {
              const label = newValue ? newValue.label || newValue : '';
              setFormValues({ ...formValues, appointmentType: label });
              if (label) {
                saveClinicalDefault('appointmentTypes', label).then(
                  setTypeOptions
                );
              }
            }}
            options={typeOptions}
            onDelete={(option) => {
              deleteClinicalDefault('appointmentTypes', option.label).then(
                setTypeOptions
              );
            }}
            helperText="Please select or enter an appointment type"
            disabled={isDisabled}
          />
        </Grid>

        <Grid size={6}>
          <Box sx={{ position: 'relative', paddingBottom: 1 }}>
            <TextInput
              label="Location"
              type="autocomplete"
              name="location"
              value={formValues.location || null}
              onChange={(e) => {
                const label = e ? e.label : '';
                setFormValues({
                  ...formValues,
                  location: label
                });
                if (label) {
                  saveClinicalDefault('appointmentLocations', label).then(
                    setLocationOptions
                  );
                }
              }}
              options={locationOptions || []}
              onDelete={(option) => {
                deleteClinicalDefault(
                  'appointmentLocations',
                  option.label
                ).then(setLocationOptions);
              }}
              helperText="Please select or enter a location"
              readOnly={isDisabled}
            />
          </Box>
        </Grid>

        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Associated Referral</FormLabel>
          </Grid>
          <AssociatedNote
            name="associatedReferral"
            handleChange={handleChange}
            value={formValues.associatedReferral}
            noteOptions={referralOptions}
            readOnly={isDisabled}
          />
        </Grid>

        <Grid size={12}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Appointment Notes</FormLabel>
          </Grid>
          <Box sx={{ width: '100%' }}>
            <TextField
              variant="outlined"
              fullWidth
              onChange={handleChange}
              name="appointmentNotes"
              value={formValues.appointmentNotes}
              multiline={true}
              slotProps={{
                input: {
                  endAdornment: formValues.appointmentNotes ? (
                    <InputAdornment position="end" sx={{ alignSelf: 'center' }}>
                      <Tooltip title="Analyse for clinical terms">
                        <span style={{ lineHeight: 0 }}>
                          <IconButton
                            size="medium"
                            onClick={() =>
                              analyzeText(formValues.appointmentNotes, 'manual')
                            }
                            disabled={analyzingNote || isDisabled}
                          >
                            <AutoAwesomeIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  ) : null
                },

                htmlInput: { readOnly: isDisabled }
              }}
            />
          </Box>
        </Grid>

        <ClinicalSuggestionsPanel
          state={clinicalSuggestions}
          onAccept={() =>
            acceptSuggestions().then((result) => {
              if (result?.success) {
                const { count = {} } = result;
                const parts = [];
                if (count.phenotypes)
                  parts.push(`${count.phenotypes} phenotype(s)`);
                if (count.genotypes)
                  parts.push(`${count.genotypes} genotype(s)`);
                if (count.diseases) parts.push(`${count.diseases} disease(s)`);
                setSnack({
                  status: true,
                  msg: `Added ${parts.join(', ')} to the record`,
                  severity: 'success'
                });
              } else {
                setSnack({
                  status: true,
                  msg: 'Failed to save suggestions',
                  severity: 'error'
                });
              }
            })
          }
          disabled={isDisabled}
        />

        <UploadFileList
          value={formValues.fileAttachments}
          familyId={familyMemberId}
          onChange={(next) => {
            // Functional: uploads can finish faster than this form re-renders.
            setFormValues((prev) => ({ ...prev, fileAttachments: next }));
            setUnsavedChanges((prev) => ({ ...prev, edit: true }));
          }}
          readOnly={isDisabled}
          onBusyChange={setUploadingFiles}
          onUploadComplete={(uploadData, name) => {
            attachmentChanges.trackUpload(name);
            const text = uploadData?.extractedText;
            if (text) handleAttachmentText(text, name);
          }}
        />

        <Grid
          container
          size={12}
          sx={{
            alignItems: 'center'
          }}
        >
          <Grid
            size={{
              xs: 6,
              sm: 7
            }}
          >
            <FormLabel component="label" className="form-label">
              Show in family
            </FormLabel>
          </Grid>
          <Grid
            container
            size={{
              xs: 6,
              sm: 3
            }}
            sx={{
              alignItems: 'center'
            }}
          >
            <Grid>
              <Switch
                color="secondary"
                checked={formValues.showInFamily ?? false}
                onChange={onToggleSwitch}
                name="showInFamily"
                disabled={isDisabled}
              />
            </Grid>
            <Grid>
              <Typography>{formValues.showInFamily ? 'Yes' : 'No'}</Typography>
            </Grid>
          </Grid>
        </Grid>

        {/* Staff Member */}
        <Grid size={12}>
          <FormControl component="fieldset" fullWidth>
            <Grid container spacing={1}>
              <Grid size={12}>
                <TextInput
                  label={'Staff Member'}
                  name="staffMember"
                  type="multiple-select"
                  value={formValues?.staffMember || []}
                  onChange={handleChange}
                  options={allStaffMembers || []}
                  mapper={staffMembersMap || {}}
                  disabled={isDisabled}
                />
              </Grid>
            </Grid>
          </FormControl>
        </Grid>

        {/* 新增：三按钮区域 */}
        {!isDisabled && (
          <Grid size={12}>
            <Box
              sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'center' }}
            >
              {/* Join 按钮 */}
              <Button
                variant="contained"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(formValues.joinUrl, '_blank');
                }}
                disabled={!formValues.joinUrl}
              >
                Join
              </Button>

              {/* Reschedule 按钮 */}
              <Button
                variant="contained"
                color="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(formValues.rescheduleUrl, '_blank');
                }}
                disabled={!formValues.rescheduleUrl}
              >
                Reschedule
              </Button>

              {/* Cancel 按钮 */}
              <Button
                variant="contained"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(formValues.cancelUrl, '_blank');
                }}
                disabled={!formValues.cancelUrl}
              >
                Cancel Appointment
              </Button>
            </Box>
          </Grid>
        )}

        {/* 提交、删除、取消 等操作按钮 */}
        <ModalButtons
          hideSaveButton={isDisabled}
          isEditing={isEditing}
          hideContinueAdding={true}
          onClick={handleSubmit}
          handleClose={handleClose}
          intermediateBtnLabel="Delete"
          intermediateAction={isEditing && !isDisabled ? handleOpenModal : null}
          addLabel={`Add new appointment`}
          saveButtonProps={
            uploadingFiles
              ? {
                  disabled: true,
                  title: 'Waiting for files to finish uploading'
                }
              : undefined
          }
        />
      </Grid>
    </form>
  );
}
